
import { useNavigate } from 'react-router-dom';
import PageNavbar from '../../../components/feature/PageNavbar';

export default function TyrostationPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                All-in-One Rehabilitation Solution
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Tyrostation is a comprehensive rehabilitation workstation that integrates multiple therapy devices, advanced software, and ergonomic design into one powerful system. This complete solution enables therapists to deliver diverse, evidence-based treatments efficiently while maintaining detailed patient records and progress tracking.
              </p>
              <p className="text-gray-700 leading-relaxed">
                With its modular design and extensive exercise library, Tyrostation adapts to various clinical settings and patient populations, providing everything needed for upper extremity, hand, cognitive, and visual-motor rehabilitation in one integrated platform.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://public.readdy.ai/ai/img_res/392b22bb5c7efe95dd680ed0da91fadb.jpg"
                alt="Tyrostation Rehabilitation Workstation"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Features</h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              <strong className="text-gray-900">Integrated Workstation:</strong> Complete rehabilitation workstation combining multiple therapy devices and software in one ergonomic setup, streamlining clinical workflows and maximizing treatment efficiency.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              <strong className="text-gray-900">Comprehensive Software Platform:</strong> Advanced software suite with extensive exercise library, assessment tools, and patient management features. Real-time monitoring provides live performance tracking and biofeedback during exercises with instant visual and audio cues.
            </p>

            <div className="grid md:grid-cols-2 gap-8 my-8">
              <div className="border-l-2 border-gray-300 pl-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Personalized Programs</h3>
                <p className="text-gray-600 leading-relaxed">
                  Customizable therapy protocols tailored to individual patient needs, goals, and progression levels.
                </p>
              </div>
              <div className="border-l-2 border-gray-300 pl-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Detailed Reporting</h3>
                <p className="text-gray-600 leading-relaxed">
                  Comprehensive reports with graphs, statistics, and progress documentation for clinical decision-making.
                </p>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Modular Design:</strong> Flexible configuration allowing integration of various Tyromotion devices based on clinic needs, ensuring the system grows with your practice.
            </p>
          </div>
        </div>
      </section>

      {/* System Components */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">System Components</h2>
          
          <div className="space-y-6">
            <div className="flex gap-6">
              <div className="w-1 bg-gray-900 flex-shrink-0"></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Dual Monitor Setup</h3>
                <p className="text-gray-700 leading-relaxed">
                  Two high-resolution displays for therapist control interface and patient exercise visualization, enabling simultaneous monitoring and patient engagement.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-1 bg-gray-900 flex-shrink-0"></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Adjustable Desk</h3>
                <p className="text-gray-700 leading-relaxed">
                  Ergonomic height-adjustable workstation accommodating patients in various positions and mobility levels, ensuring comfort during extended therapy sessions.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-1 bg-gray-900 flex-shrink-0"></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Integrated Sensors</h3>
                <p className="text-gray-700 leading-relaxed">
                  Built-in sensor devices for upper extremity, hand, and cognitive training exercises, providing precise measurement and feedback.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-1 bg-gray-900 flex-shrink-0"></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Patient Seating</h3>
                <p className="text-gray-700 leading-relaxed">
                  Comfortable adjustable seating with proper support for extended therapy sessions, promoting optimal posture and patient comfort.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Therapy Areas */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Therapy Areas</h2>
          
          <div className="space-y-10">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Upper Extremity Rehabilitation</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Comprehensive arm and shoulder training including arm reaching and coordination, shoulder range of motion, elbow flexion/extension, and bilateral arm training exercises.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Hand and Finger Training</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Targeted exercises for grip strength, fine motor control, finger isolation movements, and pinch and grasp activities to restore hand function.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Cognitive Training</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Memory exercises</li>
                  <li>• Attention and concentration</li>
                  <li>• Problem-solving tasks</li>
                  <li>• Reaction time training</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Visual-Motor Integration</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Eye-hand coordination</li>
                  <li>• Tracking exercises</li>
                  <li>• Spatial awareness tasks</li>
                  <li>• Dual-task training</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Applications */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Clinical Applications</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Tyrostation serves diverse patient populations across multiple neurological and orthopedic conditions:
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4">
            <p className="text-gray-700">Stroke Rehabilitation</p>
            <p className="text-gray-700">Traumatic Brain Injury</p>
            <p className="text-gray-700">Spinal Cord Injury</p>
            <p className="text-gray-700">Multiple Sclerosis</p>
            <p className="text-gray-700">Parkinson's Disease</p>
            <p className="text-gray-700">Orthopedic Conditions</p>
            <p className="text-gray-700">Neurological Disorders</p>
            <p className="text-gray-700">Post-Surgical Recovery</p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose Tyrostation?</h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-8">
            Tyrostation streamlines rehabilitation workflows by combining multiple therapy modalities in one integrated system. Therapists can efficiently deliver comprehensive treatment programs while maintaining detailed documentation and tracking patient progress. The modular design ensures flexibility to meet diverse clinical needs and patient populations.
          </p>
          <button
            onClick={() => navigate('/equipment')}
            className="inline-flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
          >
            <span>Explore More Equipment</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Accelerated Recovery Centre. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="nofollow noopener noreferrer"
            className="text-gray-400 text-xs hover:text-gray-600 transition-colors mt-2 inline-block"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
